import classNames from 'classnames';
import MUICheckbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import { Box } from '../Box';
import { Icon } from '../Icon';

import useStyles from './Checkbox.styles';

/**
 * Свойства компонента {@link Checkbox}
 */
export interface CheckboxProps {
  /**
   * Классы для кастомизации стилей.
   */
  className?: string;

  /**
   * Указывает, отмечен ли флажок.
   */
  checked?: boolean;

  /**
   * Указывает, отключен ли флажок.
   */
  disabled?: boolean;

  /**
   * Указывает, находится ли флажок в неопределенном состоянии.
   */
  indeterminate?: boolean;

  /**
   * Функция для обработки события изменения флажка.
   */
  onChange?: (value: boolean) => void;

  /**
   * Указывает, следует ли останавливать распространение события при клике и фокусировке.
   */
  stopPropagation?: boolean;

  /**
   * Размер вертикальной области клика для флажка.
   *
   * @defaultValue `0`
   */
  verticalClickArea?: number;

  /**
   * Необязательный идентификатор теста для флажка.
   */
  testId?: string;

  /**
   * Вариант размера флажка.
   *
   * @defaultValue `'default'`
   */
  sizeVariant?: 'default' | 'large';

  /**
   * Указывает, следует ли удалять стандартный отступ у флажка.
   *
   * @defaultValue `false`
   */
  withoutMargin?: boolean;
}

/**
 * Компонент, отображающий флажок с различными состояниями и настраиваемыми опциями.
 * Поддерживает состояния отмеченного, отключенного и неопределенного флажка, а также может обрабатывать события с возможностью остановки распространения.
 *
 * @public
 * @param className - Классы для кастомизации стилей.
 * @param checked - Указывает, отмечен ли флажок.
 * @param disabled - Указывает, отключен ли флажок.
 * @param indeterminate - Указывает, находится ли флажок в неопределенном состоянии.
 * @param onChange - Функция для обработки события изменения флажка.
 * @param stopPropagation - Указывает, следует ли останавливать распространение события при клике и фокусировке.
 * @param verticalClickArea - Размер вертикальной области клика для флажка.
 * @param testId - Необязательный идентификатор теста для флажка.
 * @param sizeVariant - Вариант размера флажка.
 * @param withoutMargin - Указывает, следует ли удалять стандартный отступ у флажка.
 * @returns Компонент Checkbox.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link CheckboxProps}
 */
export const Checkbox = ({
  className,
  checked,
  disabled,
  indeterminate,
  onChange = () => {},
  stopPropagation,
  verticalClickArea = 0,
  testId,
  withoutMargin = false,
  sizeVariant = 'default',
  ...rest
}: CheckboxProps) => {
  const { classes } = useStyles({ verticalClickArea });

  const checkbox = (
    <MUICheckbox
      {...rest}
      checked={checked}
      data-testid={testId}
      icon={
        <Box className={classes.iconUnchecked}>
          <Icon name="check_box_off" size="24" />
        </Box>
      }
      checkedIcon={
        <Box className={classes.iconChecked}>
          <Icon name="check_box_on" size="24" />
        </Box>
      }
      indeterminateIcon={
        <Box className={classes.iconChecked}>
          <Icon name="check_box_indetermanate" size="24" />
        </Box>
      }
      className={classNames(
        'aqa_checkbox',
        classes.input,
        classes.verticalClickArea,
        {
          [classes.withoutMargin]: withoutMargin,
          [classes.sizeDefault]: sizeVariant === 'default',
          [classes.sizeLarge]: sizeVariant === 'large',
        },
        className,
      )}
      color="primary"
      disabled={disabled}
      indeterminate={indeterminate}
      onChange={(event) => {
        event.stopPropagation();
        onChange(event.target.checked);
      }}
    />
  );

  if (stopPropagation) {
    return (
      <FormControlLabel
        classes={{
          root: classes.controlLabel,
        }}
        onClick={(event) => event.stopPropagation()}
        onFocus={(event) => event.stopPropagation()}
        control={checkbox}
        label=""
      />
    );
  }

  return checkbox;
};
